<template>
    <v-dialog
        :value="isOpen"
        scrollable
        max-width="700px"
        transition="slide-y-transition"
        origin="top center"
        eager
        @keydown.esc="onClose"
        @click:outside="onClose"
    >
        <v-card>
            <v-card-title>
                <template v-if="isNewPackageWare">
                    <h1 class="text-subtitle-1">
                        Dodaj towar
                    </h1>
                </template>
                <template v-else>
                    <h1 class="text-subtitle-1">
                        Edycja towaru
                    </h1>
                </template>
            </v-card-title>

            <div class="mx-5 my-1">
                <v-row>
                    <v-col cols="12" sm="7">
                        <v-select
                            :value="packageWare.calculationProduct"
                            :items="products"
                            label="Produkt"
                            item-text="name"
                            item-value="id"
                            return-object
                            outlined
                            dense
                            :hint="isNewPackageWare && isProductAmountFullAssigned ? 'Produkt jest w pełni rozpisany.' : ''"
                            persistent-hint
                            :disabled="!isNewPackageWare"
                            :error-messages="packageWareErrors"
                            @change="changeProduct"
                        />
                    </v-col>
                    <v-col cols="12" sm="5">
                        <v-text-field
                            v-model="packageWare.amount"
                            v-mask="'#######'"
                            label="Nakład"
                            outlined
                            dense
                            persistent-hint
                            :hint="packageWareAmountInfo"
                            :disabled="isNewPackageWare && isProductAmountFullAssigned"
                            :error-messages="wareAmountErrors"
                        />
                    </v-col>
                </v-row>
            </div>
            <v-divider />

            <v-card-actions>
                <v-btn
                    color="error"
                    text
                    data-sentry-label="Zamknij"
                    @click="onClose"
                >
                    Anuluj
                </v-btn>
                <v-spacer />
                <v-btn
                    color="success"
                    text
                    :data-sentry-label="isNewPackageWare ? 'Dodaj' : 'Zapisz'"
                    :disabled="(products.length === 0) || (isNewPackageWare && isProductAmountFullAssigned)"
                    @click="onSave"
                >
                    <template v-if="isNewPackageWare">
                        Dodaj
                    </template>
                    <template v-else>
                        Zapisz
                    </template>
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { required, between } from 'vuelidate/lib/validators'
import { isEmpty } from 'lodash'
import { mask } from 'vue-the-mask'

export default {
    name: 'PackageWareDialog',
    directives: { mask },
    props: {
        isOpen: {
            type: Boolean
        },
        taskData: {
            type: Object,
            default: null
        },
        productsData: {
            type: Array,
            default: () => []
        },
        packageWareData: {
            type: Object,
            default: null
        },
        shippingOrderData: {
            type: Object,
            default: null
        },
        packageData: {
            type: Object,
            default: null
        },
    },
    data() {
        return {
            packageWare: {
                calculationProduct: {},
                calculationProductId: null,
                amount: null
            },
            packagesData: [],
            shippingOrder: {},
            shippingOrderPositionPackageWaresAll: [],
            package: {}
        }
    },
    validations() {
        return {
            packageWare: {
                calculationProductId: {
                    required,
                    isUnique: value => {
                        if (!isEmpty(this.packageData) && this.isNewPackageWare) {
                            return !this.packageData.calculationImpositionTaskPackageWares.some(item => {
                                return item.calculationProductId === value
                            })
                        }

                        return true
                    }
                },
                amount: {
                    required,
                    between: between(1, this.packageWareAmountToAssign)
                }
            }
        }
    },
    computed: {
        isNewPackageWare() {
            return isEmpty(this.packageWareData)
        },
        products() {
            return this.productsData.filter(product => 
                product.calculationProductComponents?.some(component => 
                    component.calculationImpositionId !== null
                )
            ) || []
        },
        packageWareAmountInfo() {
            const productAmount = this.packageWare.amount ? this.packageWare.amount : 0

            return this.packageWare.calculationProductId ? `${productAmount} / ${this.packageWareAmountToAssign}` : ''
        },
        packageWareAmountToAssign() {
            const productAmountSum = this.packageWare.calculationProduct.amount ?? 0

            let assignedPackageWareAmount = 0

            this.packagesData.forEach(item => {
                item.calculationImpositionTaskPackageWares.forEach(item => {
                    if (item.calculationProductId === this.packageWare.calculationProductId) {
                        assignedPackageWareAmount += parseInt(item.amount)
                    }
                })
            })

            const result = productAmountSum - assignedPackageWareAmount

            if (this.isNewPackageWare) {
                return result >= 0 ? result : 0
            } else {
                return result >= 0 ? result + parseInt(this.packageWareData.amount) : this.packageWare.calculationProduct.amount
            }
        },
        isProductAmountFullAssigned() {
            return this.packageWare.calculationProductId !== null && this.packageWareAmountToAssign === 0
        },

        // Walidacje
        packageWareErrors() {
            const errors = []

            if (!this.$v.packageWare.calculationProductId.$dirty) return errors
            !this.$v.packageWare.calculationProductId.required && errors.push('Proszę wybrać produkt.')
            !this.$v.packageWare.calculationProductId.isUnique && errors.push('Wybrany produkt istnieje już w tym pakowaniu.')

            return errors
        },
        wareAmountErrors() {
            const errors = []

            if (!this.$v.packageWare.amount.$dirty) return errors

            if (!this.$v.packageWare.amount.required) {
                errors.push('Proszę podać nakład.')
            }

            if (!this.$v.packageWare.amount.required || !this.$v.packageWare.amount.between && !(this.isNewPackageWare && this.isProductAmountFullAssigned)) {
                errors.push(`Proszę podać wartość z zakresu 1-${this.packageWareAmountToAssign}`)
            }

            return errors
        }
    },
    watch: {
        isOpen(boolean) {
            if (boolean) {
                this.packagesData = JSON.parse(JSON.stringify(this.taskData.calculationImpositionTaskPackages))

                if (!this.isNewPackageWare) {
                    this.packageWare = JSON.parse(JSON.stringify(this.packageWareData))
                } else {
                    if (this.products.length === 1) {
                        this.changeProduct(this.products[0])
                    }
                }

            } else {
                setTimeout(() => {
                    this.$v.$reset()
                    this.packageWare = {
                        calculationProduct: {},
                        calculationProductId: null,
                        amount: null
                    }
                }, 500)
            }
        }
    },
    methods: {
        changeProduct(calculationProduct) {
            this.packageWare.calculationProduct = calculationProduct
            this.packageWare.calculationProductId = calculationProduct.id
            this.packageWare.amount = this.packageWareAmountToAssign
            this.$v.packageWare.calculationProductId.$touch()
        },
        onSave() {
            this.$v.$touch()

            if (this.$v.$invalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.invalidMessage')
                })

                return
            }

            let payload = JSON.parse(JSON.stringify(this.packageWare))

            this.$emit('on-save', this.packageData, payload, this.isNewPackageWare)
        },
        onClose() {
            this.$emit('on-close')
        },
    }
}
</script>